import {
  groupLinesByTimestamp,
  getTranscriptLineLabel,
  parseStructuredTranscript,
  type TranscriptLine,
} from "./transcriptParser";

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 pt-2 pb-3">
      <span className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase shrink-0">
        {label}
      </span>
      <div className="h-px flex-1 bg-gray-200" />
    </div>
  );
}

function TranscriptLineRow({
  line,
  showTimestamp,
}: {
  line: TranscriptLine;
  showTimestamp: boolean;
}) {
  const isAction = line.type === "action";

  return (
    <div className="grid grid-cols-[52px_80px_minmax(0,1fr)] gap-x-3 gap-y-1 items-start py-1.5">
      <span className="text-sm font-semibold text-foreground tabular-nums shrink-0">
        {showTimestamp ? line.time : ""}
      </span>
      <span
        className={`text-[11px] font-bold tracking-wide uppercase pt-0.5 shrink-0 ${
          isAction ? "text-orange-600" : "text-blue-600"
        }`}
      >
        {getTranscriptLineLabel(line.type)}
      </span>
      <p
        className={`text-sm leading-relaxed whitespace-pre-wrap break-words min-w-0 ${
          isAction ? "text-orange-700" : "text-foreground"
        }`}
      >
        {isAction ? line.text : `"${line.text}"`}
      </p>
    </div>
  );
}

interface TranscriptViewerProps {
  transcript: string;
}

export function TranscriptViewer({ transcript }: TranscriptViewerProps) {
  const parsed = parseStructuredTranscript(transcript);

  if (!parsed) {
    return (
      <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words bg-muted border border-border rounded-lg p-3">
        {transcript}
      </p>
    );
  }

  return (
    <div className="border border-border rounded-lg bg-card px-3 py-2">
      <p className="text-sm font-semibold text-foreground mb-1">Script</p>

      {parsed.sections.map((section) => (
        <div key={section.label}>
          <SectionDivider label={section.label} />
          {groupLinesByTimestamp(section.lines).map((group, groupIndex) => (
            <div key={`${section.label}-${groupIndex}`}>
              {group.map((line, lineIndex) => (
                <TranscriptLineRow
                  key={`${section.label}-${groupIndex}-${lineIndex}`}
                  line={line}
                  showTimestamp={lineIndex === 0}
                />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
